/**
 * Pure ranking + filtering helpers for the Blueprint card graph.
 *
 * Emergent cards are ranked by clinical relevance: clinical kinds
 * (`challenge`/`goal`/`intervention`/`lagging_skill`) float above minor kinds
 * (people, places, themes, …), then by salience descending. The filter applies
 * the provider's kind toggles + the low-salience toggle. Anchor cards are not
 * ranked or filtered here — they always show, in their existing index order.
 *
 * These helpers are pure (no React, no I/O) so they are unit-testable in
 * isolation. Nothing is logged (HIPAA): they operate only on index metadata
 * (kind, salience), never card content.
 */
import type { WikiIndexEntry } from "../../types";

/**
 * Humanize a kind/rel/label slug for a clinician-facing UI.
 *
 * Replaces underscores AND hyphens with spaces and sentence-cases the result,
 * so backend enum-like slugs read as plain text (e.g. `lagging_skill` →
 * "Lagging skill", `protective_factor` → "Protective factor", and link rels like
 * `part-of` → "Part of"). The platform serves non-technical clinicians — raw
 * ids/enum strings are never surfaced.
 *
 * @param kind - The raw kind/rel slug.
 * @returns A sentence-cased, space-separated label (empty string stays empty).
 */
export function humanizeKind(kind: string): string {
  const spaced = kind.replace(/[_-]/g, " ").trim();
  if (spaced === "") return "";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/** A raw source ref split into its source id, resolved title, and line phrase. */
export interface ParsedSourceRef {
  /** The bare source id (the part before `#`). */
  sourceId: string;
  /** The resolved human title, or null when unresolved. */
  title: string | null;
  /** A "line N" / "lines A–B" phrase, or null when there is no range. */
  lines: string | null;
}

/**
 * Parse a raw source ref into its id, resolved title, and a line phrase.
 *
 * Source refs look like `SES-1#L3-L7` or `dd00dc78-…#L101-101` — the part before
 * `#` is the source id and the `#L<a>-L<b>` suffix is the line range. The raw
 * source id is NEVER surfaced by callers (the UI serves non-technical
 * clinicians); this helper only resolves the title + a "line(s)" phrase, leaving
 * the exact label wording to the caller.
 *
 * @param ref - The raw source ref (id + optional `#L<a>-L<b>` range).
 * @param sourceTitles - Optional `{ "<source id>": "<title>" }` lookup.
 * @returns The parsed source id, resolved title (or null), and line phrase.
 */
/** Namespace marking a ref that names a live assessment record, not a span. */
export const ASSESSMENT_PREFIX = "assessment:";

/** Display names for the assessment tables the overlay cites. */
const INSTRUMENTS: Record<string, string> = {
  gad_7: "GAD-7",
  phq_9: "PHQ-9",
  vanderbilt_parent_initial: "Vanderbilt (parent, initial)",
  vanderbilt_teacher_initial: "Vanderbilt (teacher, initial)",
  vanderbilt_parent_reassessment: "Vanderbilt (parent, follow-up)",
  vanderbilt_teacher_reassessment: "Vanderbilt (teacher, follow-up)",
};

/**
 * Resolve an assessment table id to its clinician-facing instrument name.
 *
 * Falls back to a humanized form rather than the raw id, so an instrument we
 * have not enumerated still never surfaces a backend table name.
 *
 * @param table - The assessment table id from the ref.
 * @returns The display name.
 */
export function instrumentName(table: string): string {
  return INSTRUMENTS[table] ?? table.replace(/_/g, " ").toUpperCase();
}

export function parseSourceRef(
  ref: string,
  sourceTitles?: Record<string, string>,
): ParsedSourceRef {
  // A live assessment score has no document span — `assessment:<table>@<date>`
  // names a record, not a line range. Label it by instrument and date so the
  // reader sees "GAD-7, 2026-03-04" rather than a raw table id or a bare
  // "Source" with no line numbers to explain the absence.
  if (ref.startsWith(ASSESSMENT_PREFIX)) {
    const [table, date] = ref.slice(ASSESSMENT_PREFIX.length).split("@");
    return {
      sourceId: ref,
      title: instrumentName(table),
      lines: date ? `recorded ${date}` : null,
    };
  }
  const hashAt = ref.indexOf("#");
  const sourceId = (hashAt === -1 ? ref : ref.slice(0, hashAt)).trim();
  const resolved = sourceTitles?.[sourceId];
  const title = resolved !== undefined && resolved !== "" ? resolved : null;

  let lines: string | null = null;
  const sentenceMatch = /#S(\d+)(?::\s*"([^"]*)")?/.exec(ref);
  if (sentenceMatch) {
    const sentNum = sentenceMatch[1];
    lines = `sentence ${sentNum}`;
  } else {
    const range = /#L(\d+)(?:-L?(\d+))?/.exec(ref);
    if (range) {
      const start = range[1];
      const end = range[2];
      lines = end && end !== start ? `lines ${start}–${end}` : `line ${start}`;
    }
  }
  return { sourceId, title, lines };
}

/**
 * Format a raw source ref as a clinician-facing link-source label.
 *
 * Renders "{title}, line 101" when the title resolves, "{title}" when there is
 * no line range, and a neutral "Source" (or "Source · line 101") when the title
 * is unknown — NEVER the raw source id (#5).
 *
 * @param ref - The raw source ref (id + optional `#L<a>-L<b>` range).
 * @param sourceTitles - Optional `{ "<source id>": "<title>" }` lookup.
 * @returns A clean, human-readable source label — never the raw id.
 */
export function formatSourceRef(
  ref: string,
  sourceTitles?: Record<string, string>,
): string {
  const { title, lines } = parseSourceRef(ref, sourceTitles);
  if (title !== null) return lines ? `${title}, ${lines}` : title;
  return lines ? `Source · ${lines}` : "Source";
}

/** Salience words ranked by clinical centrality (higher sorts first). */
export const SALIENCE_RANK: Record<string, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

/** The clinical card kinds, which rank above all minor kinds. */
export const CLINICAL_KINDS: readonly string[] = [
  "challenge",
  "goal",
  "intervention",
  "lagging_skill",
];

/**
 * Anchor card kinds. Anchor cards (the Blueprint-section scaffold) have an
 * immutable identity, so their Type/kind is never editable.
 */
export const ANCHOR_KINDS: ReadonlySet<string> = new Set([
  "section",
  "overview",
]);

/** Report whether a card kind belongs to an immutable-identity anchor card. */
export function isAnchorKind(kind: string): boolean {
  return ANCHOR_KINDS.has(kind);
}

/** Rank a salience word for descending sort (unknown words sort last). */
export function salienceRank(salience: string): number {
  return SALIENCE_RANK[salience] ?? 0;
}

/**
 * Rank a kind by clinical priority: clinical kinds (1) above minor kinds (0).
 *
 * @param kind - The card kind slug.
 * @returns 1 for a clinical kind, 0 otherwise.
 */
export function kindPriority(kind: string): number {
  return CLINICAL_KINDS.includes(kind) ? 1 : 0;
}

/**
 * Build an id→name lookup from every card entry (anchors + emergent).
 *
 * Used to resolve a typed link's target id to the target card's human name, so
 * link rows show a readable name (never a raw `ENT-…`/node id).
 *
 * @param anchors - The anchor card entries.
 * @param byKind - The emergent cards keyed by kind.
 * @returns A map from card id to its display name.
 */
export function buildNameIndex(
  anchors: WikiIndexEntry[],
  byKind: Map<string, WikiIndexEntry[]>,
): Map<string, string> {
  const index = new Map<string, string>();
  for (const entry of anchors) index.set(entry.id, entry.name);
  for (const entries of byKind.values()) {
    for (const entry of entries) index.set(entry.id, entry.name);
  }
  return index;
}

/**
 * Resolve a link target id to a clinician-facing display name.
 *
 * Prefers the target card's real name from {@link buildNameIndex}. When the
 * target has no matching card (an unresolved/Blueprint-node id), falls back to a
 * humanized label derived from the id — the raw id is never shown.
 *
 * @param targetId - The link target id (e.g. `ENT-<slug>` or a node id).
 * @param nameIndex - The id→name lookup.
 * @returns A human-readable name for the target.
 */
export function resolveTargetName(
  targetId: string,
  nameIndex: Map<string, string>,
): string {
  const name = nameIndex.get(targetId);
  if (name !== undefined && name !== "") return name;
  // Fall back to a humanized label: strip an `ENT-`/`C-`-style prefix and the
  // patient-id segment, then sentence-case the remaining slug words.
  const tail = targetId.replace(/^[A-Za-z]+-/, "");
  const slug = tail
    .replace(/^p-?\d+-/i, "")
    .replace(/[-_]/g, " ")
    .trim();
  const label = slug === "" ? targetId : slug;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Build a resolver from a link target to the target CARD's id, for
 * click-to-reveal.
 *
 * Extraction emits a typed link's ``to`` as the target's **name** (e.g. "ADHD"),
 * not its card id, and many link targets aren't cards at all (e.g. "Ms.
 * Johnson"). This resolves a target (a card name OR an id) to the matching
 * card's id via a case-insensitive name match (or a direct id match), or
 * ``null`` when the target has no card — so the caller renders a non-card link
 * as plain text instead of a dead "reveal" click.
 *
 * @param anchors - The anchor card entries.
 * @param byKind - The emergent cards keyed by kind.
 * @returns A resolver ``(target) => cardId | null``.
 */
export function buildRevealResolver(
  anchors: WikiIndexEntry[],
  byKind: Map<string, WikiIndexEntry[]>,
): (target: string) => string | null {
  const ids = new Set<string>();
  const byName = new Map<string, string>();
  const add = (entry: WikiIndexEntry): void => {
    ids.add(entry.id);
    byName.set(entry.name.trim().toLowerCase(), entry.id);
  };
  for (const entry of anchors) add(entry);
  for (const entries of byKind.values())
    for (const entry of entries) add(entry);
  return (target: string): string | null => {
    if (ids.has(target)) return target;
    return byName.get(target.trim().toLowerCase()) ?? null;
  };
}

/** A kind group with its entries sorted by salience descending. */
export interface RankedGroup {
  /** The card kind slug. */
  kind: string;
  /** Entries of this kind, salience-descending. */
  entries: WikiIndexEntry[];
}

/**
 * Rank emergent-card kind groups by importance (top salience) descending,
 * then sort each group's entries by salience descending.
 *
 * Importance (salience) is the sole ranking key — the highest-importance
 * group always floats to the top, even when its kind is not one of the
 * clinical kinds. Groups tie on top salience (e.g. both "high") keep a
 * stable order (their existing `byKind` order, itself alphabetical by kind —
 * see {@link groupCards}); entries within a group tie the same way. This is
 * a deliberate, sort-key-only change from an earlier design that ranked
 * clinical kinds (`CLINICAL_KINDS`) above minor kinds regardless of
 * salience, which let a high-importance minor-kind card (e.g. a person)
 * render below a low-importance clinical card — the reported bug.
 * {@link kindPriority} is kept as a pure, independently-tested helper (kind
 * classification is still meaningful elsewhere, e.g. card-type UI) but no
 * longer drives this sort.
 *
 * @param byKind - Emergent cards keyed by kind.
 * @returns The ranked, salience-sorted kind groups.
 */
export function rankGroups(
  byKind: Map<string, WikiIndexEntry[]>,
): RankedGroup[] {
  const groups: RankedGroup[] = Array.from(byKind.entries()).map(
    ([kind, entries]) => ({
      kind,
      entries: [...entries].sort(
        (a, b) => salienceRank(b.salience) - salienceRank(a.salience),
      ),
    }),
  );
  groups.sort((a, b) => {
    const topA = a.entries[0] ? salienceRank(a.entries[0].salience) : 0;
    const topB = b.entries[0] ? salienceRank(b.entries[0].salience) : 0;
    return topB - topA;
  });
  return groups;
}

/** The provider's card-filter selection. */
export interface CardFilter {
  /** Kinds currently shown (a kind absent → hidden). */
  visibleKinds: Set<string>;
  /** Whether low-salience cards are shown. */
  showLowSalience: boolean;
}

/**
 * Build the default filter from the present kinds: clinical kinds ON, minor
 * kinds ON (visible but toggleable), low-salience OFF.
 *
 * @param kinds - Every emergent-card kind present for this patient.
 * @returns The pre-seeded default filter.
 */
export function defaultFilter(kinds: string[]): CardFilter {
  return { visibleKinds: new Set(kinds), showLowSalience: false };
}

/**
 * Apply the filter to ranked groups: drop hidden kinds and (unless enabled)
 * low-salience entries, then drop any group left empty.
 *
 * A non-null `forceId` always keeps the matching card visible regardless of the
 * filter — its kind is un-hidden and the entry is retained even if low-salience.
 * This guarantees a click-to-reveal target renders even when the provider's
 * filter would otherwise hide it (the user's other selections stay intact).
 *
 * @param groups - The ranked kind groups.
 * @param filter - The provider's filter selection.
 * @param forceId - An entity id / document id that must stay visible, or null.
 * @returns The visible groups after filtering.
 */
export function applyFilter(
  groups: RankedGroup[],
  filter: CardFilter,
  forceId: string | null = null,
): RankedGroup[] {
  const isForced = (e: WikiIndexEntry): boolean =>
    forceId !== null && (e.id === forceId || e.document_id === forceId);
  const visible: RankedGroup[] = [];
  for (const group of groups) {
    const groupHasForced = group.entries.some(isForced);
    if (!filter.visibleKinds.has(group.kind) && !groupHasForced) continue;
    const entries = group.entries.filter(
      (e) => filter.showLowSalience || e.salience !== "low" || isForced(e),
    );
    if (entries.length > 0) visible.push({ kind: group.kind, entries });
  }
  return visible;
}

/**
 * Count emergent cards hidden by the current filter (across all kinds).
 *
 * @param groups - The ranked kind groups (pre-filter).
 * @param visible - The post-filter visible groups.
 * @returns The number of emergent cards currently hidden.
 */
export function hiddenCount(
  groups: RankedGroup[],
  visible: RankedGroup[],
): number {
  const total = groups.reduce((sum, g) => sum + g.entries.length, 0);
  const shown = visible.reduce((sum, g) => sum + g.entries.length, 0);
  return total - shown;
}
